import React, { useState } from 'react';
import { ShieldAlert, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';

// Shared "Coach adapted X exercises for your injuries" strip.
// Consumed by both DailyCallCard and Routines views. Items are strings in the
// backend's swap-log format: "Original → Replacement :: Trigger1, Trigger2"
// (bare strings without "→" are plain skips).
export default function SafetyAdaptedPanel({ items, testid = 'safety-adapted-panel' }) {
  const [open, setOpen] = useState(false);
  const list = Array.isArray(items) ? items : [];
  const swaps = list.filter((s) => typeof s === 'string' && s.includes('→'));
  const skipped = list.filter((s) => typeof s === 'string' && !s.includes('→'));
  const swapCount = swaps.length;
  const totalCount = list.length;
  if (totalCount === 0) return null;

  const headline = swapCount > 0
    ? `Coach adapted ${swapCount} ${swapCount === 1 ? 'exercise' : 'exercises'} for your injuries`
    : `Avoided ${totalCount} today (injury-aware)`;

  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-[#3a2310] bg-[#1a0f06]/60 p-3"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between gap-2 text-left"
      >
        <span className="text-[#ff7a1a] font-bold text-[10px] uppercase tracking-wide flex items-center gap-1.5">
          <ShieldAlert className="w-3.5 h-3.5" /> {headline}
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-[#ff7a1a]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#ff7a1a]" />}
      </button>

      {!open && (
        <p className="text-[#cbb39a] text-[11px] mt-1 leading-snug">
          {swapCount > 0
            ? 'Tap to see what we swapped and why.'
            : "Tap to see what we kept out of today's call."}
        </p>
      )}

      {open && (
        <div className="mt-2 space-y-1.5" data-testid={`${testid}-details`}>
          {swaps.length > 0 && (
            <ul className="space-y-1.5">
              {swaps.map((s, i) => {
                const [namePair, triggerRaw] = s.split('::').map((p) => p.trim());
                const [from, to] = namePair.split('→').map((p) => p.trim());
                const triggers = (triggerRaw || '')
                  .split(',')
                  .map((t) => t.trim().toLowerCase())
                  .filter(Boolean);
                const reason = triggers.length === 0
                  ? null
                  : triggers.length === 1
                    ? `${triggers[0]}-safe`
                    : `safer for ${triggers.join(' + ')}`;
                return (
                  <li
                    key={`swap-${i}`}
                    data-testid={`safety-swap-${i}`}
                    className="text-[11px] text-[#cbb39a] leading-snug"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="line-through opacity-60">{from}</span>
                      <ArrowRight className="w-3 h-3 text-[#BEFF38] shrink-0" />
                      <span className="text-white font-medium">{to}</span>
                    </div>
                    {reason && (
                      <span
                        data-testid={`safety-swap-reason-${i}`}
                        className="inline-block mt-0.5 text-[9px] uppercase tracking-wider font-bold text-[#BEFF38]/80"
                      >
                        · {reason}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {skipped.length > 0 && (
            <div className="pt-1.5 border-t border-[#3a2310]">
              <p className="text-[10px] uppercase tracking-wide text-[#ff7a1a]/70 font-bold mb-1">Skipped entirely</p>
              <p className="text-[11px] text-[#cbb39a] leading-snug">
                {skipped.map((s) => s.split('::')[0].trim()).join(' · ')}
              </p>
            </div>
          )}
          <p className="pt-1.5 mt-1 text-[10px] text-[#7c6856] italic border-t border-[#3a2310]">
            Sets and reps were preserved — we kept the work, made it safer.
          </p>
        </div>
      )}
    </div>
  );
}
